import React, { Component, ReactElement } from 'react';

import { Endpoint } from '@philips-emr/hpms-provider-commons';
import { Autocomplete, Field, Fieldset, Form, Mapper, Text, TextArea } from '@philips-emr/tws-components';
import { AwesomeComponent, ComponentType, Value } from '@philips-emr/tws-core';
import { arrayOf, eventListener, object } from '@philips-emr/tws-core/lib/component/prop-types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { generateUUID, getLostProps, noop, propagateData } from '@philips-emr/tws-utils';

import HpmsFormTechnicalVisitApi from '../../api/hpms-form-technical-visit/hpms-form-technical-visit-api';

import './hpms-form-technical-visit.css';

import { HpmsFormTechnicalVisitProps, TechnicalVisitFieldMetadata } from './hpms-form-technical-visit-types';

interface HpmsQuestionTechnicalVisitProps {
  metadata: TechnicalVisitFieldMetadata;
  data?: Record<string, any>;
}

function getAnswerEntry(nrSeqFormPr: number): string {
  return `answers__${nrSeqFormPr}`;
}

function getLabelEntry(nrSeqFormPr: number): string {
  return `label__${nrSeqFormPr}`;
}

function getJustificationEntry(nrSeqFormPr: number): string {
  return `justification__${nrSeqFormPr}`;
}

class HpmsQuestionTechnicalVisit extends Component<HpmsQuestionTechnicalVisitProps> {
  static propTypes = {
    metadata: object.notEnumerable(),
    data: object.notEnumerable(),
  };

  state = {
    options: [],
  };

  componentDidMount(): void {
    const { nrSeqQuestion } = this.props.metadata;

    void HttpService.get<[]>(Endpoint.GET.FORM_TECHNICAL_VISIT, { nrSeqQuestion })
      .then(response => {
        const { body = [] } = response;

        this.setState({ options: body });
      });
  }

  isJustificationRequired(nrSeqFormPr: number): boolean {
    const { data = {} } = this.props;

    const answer = data[getAnswerEntry(nrSeqFormPr)];

    if (!answer) {
      return false;
    }

    return answer['ieRequired'] === 'S';
  }

  renderQuestion(): ReactElement {
    const { nrSeqFormPr } = this.props.metadata;
    const { options } = this.state;
    const data = options as unknown as Record<string, Value>;
    const validators = this.isJustificationRequired(nrSeqFormPr) ? [{ name: 'required', options: {} }] : [];

    return (
      <Fieldset uuid={generateUUID()} title="" entry="" data={data} onChange={noop} >
        <Field uuid={generateUUID()} width={12} alwaysShowLabel entry={getLabelEntry(nrSeqFormPr)}>
          <Text /></Field>
        <Field uuid={generateUUID()} width={6} alwaysShowLabel entry={getAnswerEntry(nrSeqFormPr)}>
          <Autocomplete
            data={options}
            key={getAnswerEntry(nrSeqFormPr)}
            entry="description"
            primaryKey="code"
            queryKey="description"
            minSearchLength={0}
            uuid={`mock_uuid_${nrSeqFormPr}`}
            localData={true}
            multiselect={false}
          >
            <Mapper map={{ value: 'description' }}>
              <Text />
            </Mapper>
          </Autocomplete>
        </Field>
        <Field uuid={generateUUID()} width={12} alwaysShowLabel label="provider.justification" entry={getJustificationEntry(nrSeqFormPr)} validators={validators}>
          <TextArea length={4000} />
        </Field>
      </Fieldset>
    );
  }

  render(): ReactElement[] {
    const lostProps = getLostProps(HpmsQuestionTechnicalVisit.propTypes, this.props);

    return propagateData(this.renderQuestion(), this.props.data, lostProps);
  }
}

@AwesomeComponent({ type: ComponentType.FORM, api: HpmsFormTechnicalVisitApi })
export default class HpmsFormTechnicalVisit extends Component<HpmsFormTechnicalVisitProps> {

  static propTypes = {
    data: arrayOf(object).notEnumerable(),
    onSubmit: eventListener,
  };

  state = {
    editing: true,
    locked: false,
    disabled: false,
  };

  renderQuestions(): ReactElement[] | null {
    const { data = [] } = this.props;

    return data.map((question, index) => (<HpmsQuestionTechnicalVisit key={index} metadata={question} />));
  }

  getData(): Record<string, any> {
    const { data = [] } = this.props;

    const record: Record<string, any> = {};

    data.forEach(item => {
      record[getAnswerEntry(item.nrSeqFormPr)] = {
        code: item.nrSeqAnswer,
        description: item.answerDescription,
        ieRequired: item.answerIeRequired,
      };
      record[getLabelEntry(item.nrSeqFormPr)] = item.dsQuestion;
      record[getJustificationEntry(item.nrSeqFormPr)] = item.dsJustification;
    });

    return record;
  }

  render(): ReactElement {
    const { onSubmit } = this.props;
    const { disabled, locked, editing } = this.state;
    return (
      <Form uuid={generateUUID()} data={this.getData()} disabled={disabled} locked={locked} editing={editing} onSubmit={onSubmit} className="c-tws-hpms-form-tecnical-visit">
        {this.renderQuestions()}
      </Form>
    );
  }
}
